import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, getDoc, setDoc, doc, updateDoc, limit, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import toast from 'react-hot-toast';
import ConfirmAction from '../../components/common/ConfirmAction';

// ═══════════════ SUPPORT CENTRE ═══════════════
export function SupportCentre() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);   // expanded ticket
  const [responses, setResponses] = useState([]);
  const [newResponse, setNewResponse] = useState('');
  const [sending, setSending] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);

  const reloadTickets = () => {
    setLoading(true);
    let q;
    if (statusFilter === 'all') {
      q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'), limit(100));
    } else {
      q = query(collection(db, 'tickets'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'), limit(100));
    }
    getDocs(q).then((snap) => { setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  };

  useEffect(() => { reloadTickets(); }, [statusFilter]);

  const loadResponses = async (ticketId) => {
    const snap = await getDocs(query(collection(db, `tickets/${ticketId}/responses`), orderBy('createdAt', 'asc')));
    setResponses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleExpand = async (ticket) => {
    setSelected(ticket);
    setResponses([]);
    await loadResponses(ticket.id);
  };

  const handleSendResponse = async () => {
    if (!newResponse.trim() || sending) return;
    setSending(true);
    try {
      const fn = httpsCallable(functions, 'addTicketResponse');
      await fn({ ticketId: selected.id, message: newResponse.trim() });
      toast.success('Response sent');
      setNewResponse('');
      await loadResponses(selected.id);
      reloadTickets();
    } catch (err) {
      toast.error(err.message || 'Send failed');
    }
    setSending(false);
  };

  const handleResolve = async (notes) => {
    try {
      const fn = httpsCallable(functions, 'markTicketResolved');
      await fn({ ticketId: resolveTarget.id, resolutionNotes: notes });
      toast.success('Ticket marked resolved');
      setResolveTarget(null);
      setSelected(null);
      reloadTickets();
    } catch (err) {
      toast.error(err.message || 'Failed to resolve');
    }
  };

  const priorityColors = { safety: '#ff6b6b', lost_item: '#fbbf24', high: '#ff6b6b', standard: '#94a3b8' };
  const statuses = ['open', 'in_progress', 'resolved', 'all'];

  return (
    <div>
      <h1 className="font-serif mt-0 mb-4">Support Centre</h1>

      <div className="flex gap-2 mb-5 flex-wrap">
        {statuses.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3.5 py-1.5 rounded-full border-[1.5px] cursor-pointer text-[0.8rem] font-semibold ${
              statusFilter === s ? 'border-brand-primary' : 'border-slate-200 bg-white'
            }`}
            style={statusFilter === s ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{s.replace('_', ' ')}</button>
        ))}
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {tickets.map((t) => {
            const isExpanded = selected?.id === t.id;
            return (
              <div key={t.id} className={`p-4 rounded-2xl bg-white border ${isExpanded ? 'border-brand-primary' : 'border-slate-200'}`}>
                <div className="cursor-pointer" onClick={() => isExpanded ? setSelected(null) : handleExpand(t)}>
                  <div className="flex justify-between mb-2">
                    <span className="font-bold">{t.referenceNumber}</span>
                    <span className="px-2.5 py-[3px] rounded-full text-[0.75rem] font-semibold"
                      style={{ color: priorityColors[t.priority] || '#94a3b8', background: `${priorityColors[t.priority] || '#94a3b8'}15` }}>{t.priority}</span>
                  </div>
                  <div className="text-[0.85rem] text-body mb-1">
                    {t.category} · <strong>{t.status?.replace('_', ' ')}</strong>
                    {t.createdAt?.toDate && <> · {new Date(t.createdAt.toDate()).toLocaleDateString()}</>}
                  </div>
                  <div className="text-[0.85rem] text-brand-dark">
                    {isExpanded ? t.description : `${t.description?.substring(0, 100)}${t.description?.length > 100 ? '...' : ''}`}
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    {/* Responses thread */}
                    {responses.length > 0 && (
                      <div className="mb-4">
                        <div className="text-[0.8rem] font-semibold text-body mb-2">Conversation</div>
                        <div className="flex flex-col gap-2">
                          {responses.map((r) => (
                            <div key={r.id} className="p-2.5 rounded-[10px]"
                              style={{
                                background: r.senderRole === 'admin' ? 'rgba(45,212,168,0.06)' : '#f8fafc',
                                borderLeft: `3px solid ${r.senderRole === 'admin' ? '#2dd4a8' : '#94a3b8'}`,
                              }}>
                              <div className="text-[0.75rem] text-body mb-1">
                                <strong>{r.senderName || r.senderRole}</strong> · {r.createdAt?.toDate ? new Date(r.createdAt.toDate()).toLocaleString() : ''}
                              </div>
                              <div className="text-[0.85rem] text-brand-dark">{r.message}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Compose response */}
                    {t.status !== 'resolved' && (
                      <div className="mb-3">
                        <textarea
                          rows={3}
                          maxLength={1000}
                          value={newResponse}
                          onChange={(e) => setNewResponse(e.target.value)}
                          placeholder="Type your response to the user..."
                          className="w-full p-2.5 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem] font-[inherit] resize-y"
                        />
                        <div className="flex gap-2 justify-end mt-2">
                          <button onClick={() => setResolveTarget(t)}
                            className="px-4 py-2 rounded-lg border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer text-[0.85rem] font-semibold">Mark Resolved</button>
                          <button onClick={handleSendResponse} disabled={!newResponse.trim() || sending}
                            className="px-4 py-2 rounded-lg border-0 text-white text-[0.85rem] font-semibold"
                            style={{
                              background: newResponse.trim() && !sending ? '#2dd4a8' : '#e2e8f0',
                              cursor: newResponse.trim() ? 'pointer' : 'not-allowed',
                            }}>{sending ? 'Sending...' : 'Send Response'}</button>
                        </div>
                      </div>
                    )}

                    {t.status === 'resolved' && (
                      <div className="p-3 rounded-lg text-[0.85rem] text-body" style={{ background: 'rgba(45,212,168,0.06)' }}>
                        ✓ Resolved {t.resolvedAt?.toDate ? `on ${new Date(t.resolvedAt.toDate()).toLocaleDateString()}` : ''}
                        {t.resolutionNotes && <div className="mt-1 text-brand-dark"><em>{t.resolutionNotes}</em></div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {tickets.length === 0 && <div className="p-8 text-center text-muted bg-white rounded-2xl border border-slate-200">No tickets in this category</div>}
        </div>
      )}

      {resolveTarget && (
        <ConfirmAction
          title="Mark ticket as resolved?"
          description={`Ticket ${resolveTarget.referenceNumber} will be closed. The user will be notified.`}
          confirmLabel="Mark Resolved"
          reasonRequired={false}
          destructive={false}
          onConfirm={handleResolve}
          onCancel={() => setResolveTarget(null)}
        />
      )}
    </div>
  );
}

// ═══════════════ LOST ITEMS PANEL ═══════════════
export function LostItemsPanel() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'lostItems'), orderBy('createdAt', 'desc'), limit(100)))
      .then((snap) => { setCases(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  }, []);

  const handleConfirmMatch = async (caseId) => {
    try {
      const fn = httpsCallable(functions, 'confirmLostItemMatch');
      await fn({ caseId });
      toast.success('Match confirmed');
      setCases((prev) => prev.map((c) => c.id === caseId ? { ...c, itemMatchConfirmed: true, status: 'confirmed' } : c));
    } catch (err) { toast.error(err.message); }
  };

  const handleShareContacts = async (caseId) => {
    try {
      const fn = httpsCallable(functions, 'shareLostItemContacts');
      await fn({ caseId });
      toast.success('Contact details shared with both parties');
      setCases((prev) => prev.map((c) => c.id === caseId ? { ...c, contactShared: true, status: 'return_arranged' } : c));
    } catch (err) { toast.error(err.message); }
  };

  const statusColors = { reported: '#fbbf24', under_review: '#6366f1', confirmed: '#2dd4a8', return_arranged: '#8b5cf6', resolved: '#5a6578', unresolved: '#ff6b6b' };

  return (
    <div>
      <h1 style={{ fontFamily: "'DM Serif Display', serif", margin: '0 0 24px' }}>Lost Items</h1>
      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div> : (
        <div style={{ display: 'grid', gap: 16 }}>
          {cases.map((c) => (
            <div key={c.id} style={{ padding: 20, borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontWeight: 700 }}>{c.referenceNumber}</span>
                <span style={{ padding: '3px 10px', borderRadius: 100, fontSize: '0.75rem', fontWeight: 600, color: statusColors[c.status], background: `${statusColors[c.status]}15` }}>{c.status}</span>
              </div>

              {/* Side-by-side reports */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#5a6578', marginBottom: 4 }}>RIDER REPORT</div>
                  <div style={{ fontSize: '0.85rem' }}>{c.riderReport?.description || 'Not yet reported'}</div>
                </div>
                <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#5a6578', marginBottom: 4 }}>DRIVER REPORT</div>
                  <div style={{ fontSize: '0.85rem' }}>{c.driverReport?.description || 'Not yet reported'}</div>
                </div>
              </div>

              {/* Admin Actions */}
              <div style={{ display: 'flex', gap: 8 }}>
                {!c.itemMatchConfirmed && c.riderReport && c.driverReport && (
                  <button onClick={() => handleConfirmMatch(c.id)} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>Confirm Match</button>
                )}
                {c.itemMatchConfirmed && !c.contactShared && (
                  <button onClick={() => handleShareContacts(c.id)} style={{
                    padding: '8px 16px', borderRadius: 100, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                  }}>Share Contact Details</button>
                )}
              </div>
            </div>
          ))}
          {cases.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No lost item cases</div>}
        </div>
      )}
    </div>
  );
}

// ═══════════════ DAMAGE CLAIMS PANEL ═══════════════
export function DamageClaimsPanel() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'claims'), orderBy('createdAt', 'desc'), limit(100)))
      .then((snap) => { setClaims(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  }, []);

  return (
    <div>
      <h1 style={{ fontFamily: "'DM Serif Display', serif", margin: '0 0 24px' }}>Damage Claims</h1>
      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div> : (
        <div style={{ display: 'grid', gap: 12 }}>
          {claims.map((c) => (
            <div key={c.id} style={{ padding: 16, borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700 }}>{c.referenceNumber}</span>
                <span style={{ fontWeight: 700, color: '#0a1628' }}>${c.amount?.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#5a6578' }}>{c.type} · {c.status}</div>
              <div style={{ fontSize: '0.85rem', marginTop: 4 }}>{c.description?.substring(0, 100)}...</div>
            </div>
          ))}
          {claims.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No claims</div>}
        </div>
      )}
    </div>
  );
}

// ═══════════════ SAFETY DASHBOARD ═══════════════
export function SafetyDashboard() {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    getDocs(query(collection(db, 'sosAlerts'), orderBy('timestamp', 'desc'), limit(50)))
      .then((snap) => setAlerts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Safety Dashboard</h1>
      <div className="grid gap-3">
        {alerts.map((a) => (
          <div key={a.id} className="p-4 rounded-2xl"
            style={{
              background: a.status === 'active' ? 'rgba(255,107,107,0.06)' : '#fff',
              border: `1px solid ${a.status === 'active' ? '#ff6b6b' : '#e2e8f0'}`,
            }}>
            <div className="flex justify-between">
              <span className="font-bold" style={{ color: a.status === 'active' ? '#ff6b6b' : '#0a1628' }}>🚨 SOS Alert</span>
              <span className="text-[0.8rem] text-muted">{a.timestamp?.toDate ? new Date(a.timestamp.toDate()).toLocaleString() : ''}</span>
            </div>
            <div className="text-[0.85rem] text-body mt-1">
              Triggered by: {a.triggeredBy} · Booking: {a.bookingId} · Status: {a.status}
            </div>
          </div>
        ))}
        {alerts.length === 0 && <div className="p-8 text-center text-muted">No SOS alerts</div>}
      </div>
    </div>
  );
}

// ═══════════════ FARE CONFIG ═══════════════
const DEFAULT_SPLIT_BANDS = [
  { maxRatePercent: 1, driverPercent: 92.5 },
  { maxRatePercent: 2, driverPercent: 87.5 },
  { maxRatePercent: 3, driverPercent: 85 },
  { maxRatePercent: 4, driverPercent: 80 },
  { maxRatePercent: 999, driverPercent: 72.5 },
];

export function FareConfigPanel() {
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bands, setBands] = useState(null);
  const [savingBands, setSavingBands] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'config', 'fareRates')).then((snap) => {
      setRates(snap.exists() ? snap.data() : {
        tiers: { go: { baseFare: 8, perKm: 1.8, perMin: 0.45 }, comfy: { baseFare: 12, perKm: 2.4, perMin: 0.55 }, plus: { baseFare: 15, perKm: 2.8, perMin: 0.65 }, vip: { baseFare: 25, perKm: 4.0, perMin: 0.85 } },
        levy: 1.2, gstRate: 0.1, minFare: 12, waitingFreeMinutes: 5, waitingFeePerMin: 1.1,
      });
    });
    getDoc(doc(db, 'config', 'bookingRules')).then((snap) => {
      const b = snap.exists() && Array.isArray(snap.data().driverSplitBands) && snap.data().driverSplitBands.length
        ? snap.data().driverSplitBands : DEFAULT_SPLIT_BANDS;
      setBands(b.map((x) => ({ ...x })));
    });
  }, []);

  const updateTierRate = (tier, field, value) => {
    setRates((prev) => ({ ...prev, tiers: { ...prev.tiers, [tier]: { ...prev.tiers[tier], [field]: parseFloat(value) || 0 } } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'fareRates'), { ...rates, updatedAt: serverTimestamp() });
      toast.success('Fare rates updated');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const updateBand = (i, value) => {
    setBands((prev) => prev.map((b, idx) => (idx === i ? { ...b, driverPercent: parseFloat(value) || 0 } : b)));
  };

  const handleSaveBands = async () => {
    setSavingBands(true);
    try {
      // merge — the quality config also lives in config/bookingRules and must be preserved
      await setDoc(doc(db, 'config', 'bookingRules'), { driverSplitBands: bands, updatedAt: serverTimestamp() }, { merge: true });
      toast.success('Driver split bands updated');
    } catch (err) { toast.error(err.message); }
    setSavingBands(false);
  };

  const bandLabel = (i) => {
    const lo = i === 0 ? 0 : bands[i - 1].maxRatePercent;
    const hi = bands[i].maxRatePercent;
    return hi >= 999 ? `${lo}%+` : `${lo}% to <${hi}%`;
  };

  if (!rates) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Fare Configuration</h1>
      {Object.entries(rates.tiers).map(([tier, r]) => (
        <div key={tier} className="p-5 rounded-2xl bg-white border border-slate-200 mb-4">
          <h3 className="mt-0 mb-3 capitalize">Reservo {tier}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="form-group"><label className="form-label">Base Fare ($)</label><input className="form-input" type="number" step="0.5" value={r.baseFare} onChange={(e) => updateTierRate(tier, 'baseFare', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Per Km ($)</label><input className="form-input" type="number" step="0.1" value={r.perKm} onChange={(e) => updateTierRate(tier, 'perKm', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Per Min ($)</label><input className="form-input" type="number" step="0.05" value={r.perMin} onChange={(e) => updateTierRate(tier, 'perMin', e.target.value)} /></div>
          </div>
        </div>
      ))}
      <button onClick={handleSave} disabled={saving} className="btn btn-primary mt-3">
        {saving ? 'Saving...' : 'Save Fare Rates'}
      </button>

      <h2 className="font-serif mt-10 mb-2">Driver earnings split</h2>
      <p className="text-body text-[0.9rem] mb-4">
        The driver&apos;s percentage of the eligible fare on each completed trip, set by their cancellation rate at trip start —
        lower cancellation earns a higher share. Tolls always pass to the driver in full; CTP, passenger levy and booking fee are retained before the split.
      </p>
      {!bands ? <div className="p-6 text-center text-muted">Loading split bands…</div> : (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-4">
          {bands.map((b, i) => (
            <div key={i} className="flex items-center gap-3 mb-2">
              <div className="flex-1 text-[0.9rem] text-body">Cancellation {bandLabel(i)}</div>
              <div className="form-group m-0" style={{ width: 130 }}>
                <input className="form-input" type="number" step="0.5" value={b.driverPercent}
                  onChange={(e) => updateBand(i, e.target.value)} />
              </div>
              <span className="text-muted text-[0.85rem] w-24">% to driver</span>
            </div>
          ))}
          <div className="text-[0.75rem] text-muted mt-2">
            Matched as &ldquo;cancellation rate &lt; threshold&rdquo;, in order; the last band is the floor.
            To change the thresholds themselves, edit config/bookingRules.driverSplitBands directly.
          </div>
          <button onClick={handleSaveBands} disabled={savingBands} className="btn btn-primary mt-3">
            {savingBands ? 'Saving...' : 'Save Split Bands'}
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════ FINANCIAL REPORTS ═══════════════
export function FinancialReports() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getDocs(query(collection(db, 'bookings'), where('status', '==', 'completed'), orderBy('completedAt', 'desc'), limit(1000)))
      .then((snap) => {
        let totalRevenue = 0, totalGST = 0, totalLevy = 0, totalDriver = 0, totalPlatform = 0, settledCount = 0;
        const list = [];
        snap.docs.forEach((d) => {
          const b = d.data();
          const fare = b.fareBreakdown || {};
          const st = b.settlement || null;
          totalRevenue += fare.totalFare || 0;
          totalGST += fare.gst || 0;
          totalLevy += fare.levy || 0;
          if (st) { totalDriver += st.driverTotal || 0; totalPlatform += st.platformTotal || 0; settledCount++; }
          if (list.length < 200) {
            list.push({ id: d.id, ref: b.referenceNumber || d.id, completedAt: b.completedAt, riderPaid: fare.totalFare || 0, settlement: st });
          }
        });
        setSummary({ totalRevenue, totalGST, totalLevy, totalDriver, totalPlatform, tripCount: snap.size, settledCount });
        setRows(list);
      });
  }, []);

  if (!summary) return <div className="p-10 text-center">Loading...</div>;

  const items = [
    { label: 'Total Revenue', value: `$${summary.totalRevenue.toFixed(2)}`, color: '#2dd4a8' },
    { label: 'GST Collected', value: `$${summary.totalGST.toFixed(2)}`, color: '#fbbf24' },
    { label: 'Levy Collected', value: `$${summary.totalLevy.toFixed(2)}`, color: '#6366f1' },
    { label: 'Driver Payouts', value: `$${summary.totalDriver.toFixed(2)}`, color: '#059669' },
    { label: 'Platform Net', value: `$${summary.totalPlatform.toFixed(2)}`, color: '#8b5cf6' },
    { label: 'Completed Trips', value: summary.tripCount, color: '#0a1628' },
  ];

  const fmtD = (ts) => (ts?.toDate ? ts.toDate().toLocaleDateString('en-AU') : (ts?._seconds ? new Date(ts._seconds * 1000).toLocaleDateString('en-AU') : '—'));

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Financial Reports</h1>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {items.map((item) => (
          <div key={item.label} className="p-5 rounded-2xl bg-white border border-slate-200">
            <div className="text-[0.85rem] text-body mb-2">{item.label}</div>
            <div className="text-[1.5rem] font-extrabold" style={{ color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-[0.8rem] text-muted">
        {summary.settledCount} of {summary.tripCount} completed trips carry a per-trip settlement. For settled trips, driver payouts + platform net reconcile to total revenue.
      </div>

      <h2 className="font-serif mt-8 mb-3">Trip settlements</h2>
      {rows.length === 0 ? <div className="p-6 text-center text-muted">No completed trips.</div> : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="flex px-4 py-2 bg-slate-50 text-[0.72rem] font-bold text-muted">
            <div className="flex-1">Trip</div>
            <div className="w-24 text-right">Rider paid</div>
            <div className="w-24 text-right">Driver</div>
            <div className="w-24 text-right">Platform</div>
          </div>
          {rows.map((row) => <SettlementRow key={row.id} row={row} fmtD={fmtD} />)}
        </div>
      )}
    </div>
  );
}

function SettlementRow({ row, fmtD }) {
  const [open, setOpen] = useState(false);
  const st = row.settlement;
  return (
    <div style={{ borderTop: '1px solid #f1f5f9' }}>
      <div className={`flex items-center px-4 py-2.5 text-[0.85rem] ${st ? 'cursor-pointer' : ''}`} onClick={st ? () => setOpen((v) => !v) : undefined}>
        <div className="flex-1">
          <span className="font-semibold text-brand-dark">{row.ref}</span>
          <span className="text-muted text-[0.78rem] ml-2">{fmtD(row.completedAt)}</span>
          {st && <span className="text-muted text-[0.7rem] ml-2">{open ? '\u25B2' : '\u25BC'}</span>}
        </div>
        <div className="w-24 text-right text-body">${row.riderPaid.toFixed(2)}</div>
        <div className="w-24 text-right" style={{ color: '#059669' }}>{st ? `$${(st.driverTotal || 0).toFixed(2)}` : '\u2014'}</div>
        <div className="w-24 text-right" style={{ color: '#8b5cf6' }}>{st ? `$${(st.platformTotal || 0).toFixed(2)}` : '\u2014'}</div>
      </div>
      {st && open && (
        <div className="px-4 pb-3 grid md:grid-cols-2 gap-3">
          <InvoiceMini title={`Driver \u2014 ${st.driverPercent}% (cancel ${st.cancellationRatePercent}%)`} invoice={st.driverInvoice} />
          <InvoiceMini title="Platform" invoice={st.platformInvoice} />
        </div>
      )}
    </div>
  );
}

function InvoiceMini({ title, invoice }) {
  return (
    <div className="p-3 rounded-xl bg-slate-50">
      <div className="text-[0.78rem] font-bold text-brand-dark mb-1">{title}</div>
      {(invoice?.lines || []).map((l, i) => (
        <div key={i} className="flex justify-between text-[0.78rem] py-0.5">
          <span className="text-muted">{l.label}</span>
          <span className="text-body">${(l.amount || 0).toFixed(2)}</span>
        </div>
      ))}
      <div className="flex justify-between text-[0.8rem] font-bold pt-1 mt-1 text-brand-dark" style={{ borderTop: '1px solid #e2e8f0' }}>
        <span>Total</span><span>${(invoice?.total || 0).toFixed(2)}</span>
      </div>
    </div>
  );
}

// ═══════════════ AUDIT LOG ═══════════════
export function AuditLogPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'auditLog'), orderBy('timestamp', 'desc'), limit(200)))
      .then((snap) => { setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  }, []);

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Audit Log</h1>
      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-auto">
          <table className="w-full border-collapse text-[0.85rem]">
            <thead><tr className="bg-slate-50">
              {['Timestamp', 'Action', 'User ID', 'Details'].map((h) => (
                <th key={h} className="px-3.5 py-3 text-left font-semibold text-body border-b border-slate-200">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="px-3.5 py-2 text-[0.8rem] text-muted">{e.timestamp?.toDate ? new Date(e.timestamp.toDate()).toLocaleString() : ''}</td>
                  <td className="px-3.5 py-2 font-semibold">{e.action}</td>
                  <td className="px-3.5 py-2 text-[0.8rem]">{e.userId || e.performedBy || '—'}</td>
                  <td className="px-3.5 py-2 text-[0.8rem] max-w-[300px] overflow-hidden text-ellipsis">{JSON.stringify(e.details || {}).substring(0, 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



// ═══════════════ FLAGGED MESSAGES PANEL ═══════════════
export function FlaggedMessagesPanel() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actioningFlag, setActioningFlag] = useState(null); // { flag, action }

  const loadFlags = () => {
    setLoading(true);
    let q;
    if (statusFilter === 'pending') {
      // pending = no `status` field set yet (un-reviewed)
      q = query(collection(db, 'flaggedMessages'), orderBy('timestamp', 'desc'), limit(200));
    } else {
      q = query(collection(db, 'flaggedMessages'), where('status', '==', statusFilter), orderBy('timestamp', 'desc'), limit(200));
    }
    getDocs(q).then((snap) => {
      let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (statusFilter === 'pending') {
        // Client-side filter to only un-reviewed (no status field)
        docs = docs.filter((d) => !d.status);
      }
      setFlags(docs);
      setLoading(false);
    });
  };

  useEffect(() => { loadFlags(); }, [statusFilter]);

  const handleAction = async (flag, action) => {
    setActioningFlag({ flag, action });
  };

  const performAction = async (notes) => {
    const { flag, action } = actioningFlag;
    try {
      const fn = httpsCallable(functions, 'reviewFlaggedMessage');
      await fn({ flagId: flag.id, action, notes });
      toast.success(`Marked as ${action.replace('_', ' ')}`);
      setActioningFlag(null);
      loadFlags();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
  };

  const labelColors = {
    phone_number: '#dc2626',
    email: '#dc2626',
    alt_comms: '#fbbf24',
    off_platform_payment: '#dc2626',
    circumvention: '#fbbf24',
  };
  const labelText = {
    phone_number: 'Phone',
    email: 'Email',
    alt_comms: 'Alt comms',
    off_platform_payment: 'Off-platform pay',
    circumvention: 'Circumvention',
  };

  const statuses = ['pending', 'reviewed_safe', 'reviewed_violation', 'escalated'];

  return (
    <div>
      <h1 className="font-serif mt-0 mb-3">Flagged Messages</h1>
      <p className="text-body text-[0.9rem] mb-5">
        Messages blocked by the chat content filter — review for fraud, off-platform coordination, or other violations.
      </p>

      <div className="flex gap-2 mb-5 flex-wrap">
        {statuses.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3.5 py-1.5 rounded-full border-[1.5px] cursor-pointer text-[0.8rem] font-semibold ${
              statusFilter === s ? 'border-brand-primary' : 'border-slate-200 bg-white'
            }`}
            style={statusFilter === s ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{s.replace('_', ' ')}</button>
        ))}
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {flags.length === 0 && (
            <div className="p-8 text-center text-muted bg-white rounded-2xl border border-slate-200">
              No flagged messages in this category
            </div>
          )}
          {flags.map((flag) => (
            <div key={flag.id} className="p-4 rounded-2xl bg-white border border-slate-200">
              <div className="flex justify-between items-start mb-2.5">
                <div className="flex-1">
                  <div className="flex gap-2 items-center mb-1">
                    <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold"
                      style={{ background: `${labelColors[flag.label] || '#94a3b8'}15`, color: labelColors[flag.label] || '#94a3b8' }}>
                      {labelText[flag.label] || flag.label}
                    </span>
                    <span className="text-[0.75rem] text-muted">
                      {flag.userRole} · {flag.timestamp?.toDate ? new Date(flag.timestamp.toDate()).toLocaleString() : ''}
                    </span>
                  </div>
                  <div className="text-[0.85rem] text-brand-dark mb-1.5 p-2.5 bg-slate-50 rounded-lg font-mono">
                    "{flag.text}"
                  </div>
                  <div className="text-[0.75rem] text-body">
                    User: <span className="font-mono">{flag.userId}</span>
                    {flag.bookingId && <> · Booking: <span className="font-mono">{flag.bookingId.substring(0, 8)}…</span></>}
                  </div>
                </div>
              </div>

              {flag.status ? (
                <div className="pt-2 border-t border-slate-100 text-[0.8rem] text-body">
                  <strong>Status:</strong> {flag.status.replace('_', ' ')}
                  {flag.reviewNotes && <div className="mt-1"><em>Notes:</em> {flag.reviewNotes}</div>}
                </div>
              ) : (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => handleAction(flag, 'reviewed_safe')}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.8rem] font-semibold">Mark Safe</button>
                  <button onClick={() => handleAction(flag, 'reviewed_violation')}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] cursor-pointer text-[0.8rem] font-semibold"
                    style={{ borderColor: '#fbbf24', background: '#fffbeb', color: '#92400e' }}>Violation</button>
                  <button onClick={() => handleAction(flag, 'escalated')}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] border-red-600 cursor-pointer text-[0.8rem] font-semibold"
                    style={{ background: '#fef2f2', color: '#dc2626' }}>Escalate</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {actioningFlag && (
        <ConfirmAction
          title={`${actioningFlag.action.replace('_', ' ')} this flag?`}
          description={`This action will be logged in the audit log.`}
          confirmLabel="Confirm"
          reasonRequired={actioningFlag.action !== 'reviewed_safe'}
          destructive={actioningFlag.action === 'escalated'}
          onConfirm={performAction}
          onCancel={() => setActioningFlag(null)}
        />
      )}
    </div>
  );
}